'use client';

import { useCallback, useEffect, useState } from 'react';
import { authService } from '@/lib/authService';
import {
  EXTERNAL_ADAPTER_METAS,
  type SafeAdapterConfig,
  type AdapterMeta,
} from '@/lib/finance/automation/publishAdapter';

export interface ConnectedAdapter {
  config: SafeAdapterConfig;
  meta: AdapterMeta;
}

/**
 * Fetch the list of connected external accounting adapters for the current
 * client. Returns only adapters that are both `connected: true` AND have a
 * matching entry in `EXTERNAL_ADAPTER_METAS` (so the UI can render logos and
 * labels).
 *
 * The fetch is lightweight (single GET /api/v1/finance/automation/adapters)
 * and cached for the component's lifetime. Callers can force a refresh via
 * the returned `refresh` function.
 */
export function useConnectedAdapters(): {
  adapters: ConnectedAdapter[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [configs, setConfigs] = useState<SafeAdapterConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const res = await fetch('/api/v1/finance/automation/adapters', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) {
            setConfigs([]);
            setLoading(false);
          }
          return;
        }
        const json = await res.json();
        if (cancelled) return;
        const data = json?.success && Array.isArray(json.data) ? json.data as SafeAdapterConfig[] : [];
        setConfigs(data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load adapters');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [nonce]);

  const adapters: ConnectedAdapter[] = configs
    .filter((c) => c.connected)
    .map((config) => {
      const meta = EXTERNAL_ADAPTER_METAS.find((m) => m.id === config.id);
      return meta ? { config, meta } : null;
    })
    .filter((a): a is ConnectedAdapter => a !== null);

  return { adapters, loading, error, refresh };
}
